import { CodeBlock, dracula } from "@react-email/code-block";
import { Button, Icon, Modal, Typography } from "@/components/atoms";
import { getWidgetHostUrl } from "@/helpers/apiHelpers";
import { useOperator } from "@/hooks";
import { copyToClipboard } from "@/utils";
import { WidgetTheme } from "./type";

interface WidgetModalProps {
  open: boolean;
  onClose: () => void;
  theme: WidgetTheme;
}

export const WidgetModal = ({ open, onClose, theme }: WidgetModalProps) => {
  const { id } = useOperator();
  const widgetHost = getWidgetHostUrl();
  // embed.js mounts a same-origin iframe on the div and auto-resizes it via postMessage,
  // so the embedding host gets the auto-fit behaviour for free. data-theme="dark" selects
  // the dark widget; light omits it. The button colour comes from the operator's saved
  // widget colour (no attribute needed).
  const code = `<div data-rm-quote data-provider="${id}"${theme === "dark" ? ' data-theme="dark"' : ""}></div>
<script src="${widgetHost}/instant-quote/embed.js" async></script>`;

  const renderHeader = () => (
    <div className="sm:flex sm:items-start">
      <div className="mt-3 w-full text-center sm:mt-0 sm:text-left">
        <div className="flex flex-row items-center">
          <Typography variant="title" className="flex-1">
            {theme === "dark" ? "Dark Mode Widget Code" : "Light Mode Widget Code"}
          </Typography>
          <Icon name="close" isCustom className="flex cursor-pointer justify-end" onClick={onClose} />
        </div>
      </div>
    </div>
  );

  const renderButtons = () => (
    <div className="mt-8 flex flex-col">
      <Button size="lg" variant="primary" onClick={() => copyToClipboard(code)}>
        Copy HTML Code
      </Button>
    </div>
  );

  return (
    <Modal open={open} onClose={onClose} className="w-[85vw] md:w-[550px] lg:w-[550px]">
      {renderHeader()}
      <Typography variant="paragraph" className="mb-6 mt-3 text-neutral-dark-gray">
        Paste this snippet where you want the {theme === "dark" ? "dark" : "light"} instant-quote widget to appear on your website. Visitors
        can request transport quotations without leaving your page.
      </Typography>
      <CodeBlock style={{ textWrap: "wrap" }} code={code} theme={dracula} language="markup" />
      {renderButtons()}
    </Modal>
  );
};
